import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';
import LearningDetailCard from '@/components/organisms/Card/LearningDetailCard';
import { studentHomeworkApi } from '@/services/api';

interface UnitProgress {
    id: number;
    code: string;
    title: string;
    unitType: string;
    unitCategory: string;
    orderIndex: number;
    status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
    progress: number;
    score?: number;
    lastAccessedDate?: string;
    textbookId?: number;
    textbookTitle?: string;
    textbookName?: string;
}

interface LearningDetail {
    id: number;
    title: string;
    startDate: string;
    endDate: string;
    status: 'completed' | 'in-progress' | 'waiting';
    overallProgress: number;
    totalUnits: number;
    completedUnits: number;
    maxUnitsPerDay?: number;
    dailyLimit?: any;
    scoringRequired?: boolean;
    units: UnitProgress[];
}

// 학습 상세
export default function StudentLearningDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const filterTextbookId = Number(searchParams.get('textbookId') || 0);
    const filterTextbookTitle = (searchParams.get('textbookTitle') || searchParams.get('textbookName') || '').trim();
    const slugPrefix = getSlugPrefix(pathname);
    const [learningDetail, setLearningDetail] = useState<LearningDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // 학습 상세 정보 조회
    const fetchLearningDetail = async () => {
        if (!id) return;
        
        setIsLoading(true);
        setError('');
        
        try {
            const response = await studentHomeworkApi.detail(Number(id));
            
            if (response.success) {
                const raw = response.data || {};
                // textbooks -> units 평탄화
                const textbooks = Array.isArray(raw.textbooks) ? raw.textbooks : [];
                const flatUnits: UnitProgress[] = [];
                textbooks.forEach((tb: any) => {
                    const tbId = Number(tb?.textbookId ?? tb?.id ?? 0);
                    const tbTitle = String(tb?.textbookTitle ?? tb?.title ?? '');
                    const units = Array.isArray(tb?.units) ? tb.units : [];
                    units.forEach((u: any, idx: number) => {
                        flatUnits.push({
                            id: Number(u.unitId ?? u.id ?? idx),
                            code: String(u.unitCode ?? ''),
                            title: String(u.unitTitle ?? ''),
                            unitType: String(u.unitType ?? ''),
                            unitCategory: String(u.unitCategory ?? ''),
                            orderIndex: Number(u.orderIndex ?? idx),
                            status: String(u.status ?? 'NOT_STARTED') as UnitProgress['status'],
                            progress: Number(u.progress ?? 0),
                            score: u.score != null ? Number(u.score) : undefined,
                            textbookId: tbId || undefined,
                            textbookTitle: tbTitle || undefined,
                        });
                    });
                });

                let filteredUnits =
                    filterTextbookId > 0
                        ? flatUnits.filter(u => (u.textbookId ?? -1) === filterTextbookId)
                        : (filterTextbookTitle
                            ? flatUnits.filter(u => {
                                const a = (u.textbookTitle || u.textbookName || '').toLowerCase().trim();
                                return a === filterTextbookTitle.toLowerCase();
                              })
                            : flatUnits);

                // 필터 결과가 비었지만 실제 유닛은 존재하는 경우 전체 목록으로 복원
                if (filteredUnits.length === 0 && flatUnits.length > 0) {
                    filteredUnits = flatUnits;
                }
                const mapped: LearningDetail = {
                    id: Number(raw.scheduleId ?? id),
                    title: String(raw.scheduleTitle ?? ''),
                    startDate: String(raw.startDate ?? ''),
                    endDate: String(raw.endDate ?? ''),
                    status: String(raw.status ?? 'waiting') as LearningDetail['status'],
                    overallProgress: Number(raw.progress ?? raw.overallProgress ?? 0),
                    totalUnits: Number(raw.totalUnits ?? filteredUnits.length ?? 0),
                    completedUnits: Number(raw.completedUnits ?? 0),
                    maxUnitsPerDay: raw.maxUnitsPerDay != null ? Number(raw.maxUnitsPerDay) : undefined,
                    dailyLimit: raw.dailyLimit,
                    scoringRequired: Boolean(raw.isHomework),
                    units: filteredUnits,
                };
                setLearningDetail(mapped);
            } else {
                setError(response.message || '학습 정보를 불러오는데 실패했습니다.');
            }
        } catch (err: any) {
            console.error('학습 상세 조회 에러:', err);
            const errorMessage = err?.response?.data?.message || '학습 정보를 불러오는 중 오류가 발생했습니다.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLearningDetail();
    }, [id, filterTextbookId, filterTextbookTitle]);

    if (isLoading) {
        return (
            <main className='flex flex-1 justify-center items-center w-full bg-white lg:my-10 card md:py-5 md:bg-transparent'>
                <div className='text-center py-10 text-gray-500'>
                    학습 정보를 불러오는 중...
                </div>
            </main>
        );
    }

    if (error || !learningDetail) {
        return (
            <main className='flex flex-1 justify-center items-center w-full bg-white lg:my-10 card md:py-5 md:bg-transparent'>
                <div className='text-center py-10'>
                    <p className='text-red-500 mb-4'>{error || '학습을 찾을 수 없습니다.'}</p>
                    <button 
                        onClick={() => navigate(`${slugPrefix}/learning/list`)}
                        className='text-blue-500 hover:underline'
                    >
                        목록으로 돌아가기
                    </button>
                </div>
            </main>
        );
    }

return (    
    <main className='flex flex-1 justify-center w-full bg-white lg:my-10 card md:py-5 md:bg-transparent'>
        <div className='flex flex-col w-full'>
            <div className='px-5 pt-6 pb-3 lg:pt-0'>
                <button 
                    onClick={() => navigate(`${slugPrefix}/learning/list`)}
                    className='text-sm text-gray-500 hover:text-gray-700 mb-2'
                >
                    ← 목록으로
                </button>
                <h1 className='heading1'>{learningDetail.title}</h1>
                <p className='text-sm text-gray-600 mt-1'>
                    {learningDetail.startDate} ~ {learningDetail.endDate}
                </p>
            </div>

            <div className='flex flex-col gap-4 px-5 pb-3'>
                <LearningDetailCard 
                    status={learningDetail.status} 
                    progress={learningDetail.overallProgress}
                    type='learning'
                    units={learningDetail.units}
                    scheduleId={learningDetail.id}
                    dailyLimit={learningDetail.dailyLimit}
                />
            </div>
        </div>
    </main>
  )
}